/**
 * SemanticSearchModal — global ⌘K search across every artifact.
 *
 * Search runs against the embeddings table via /v1/search/semantic.
 * Returns clients, deals, emails, contracts, memories, actions, etc.
 * Each result has a similarity score and a deep-link.
 *
 * Mounted once at the top of App; hotkey listener fires on Cmd/Ctrl+K.
 * If a chat panel is already bound to ⌘K, this modal takes precedence
 * (chat dispatches its own event and we don't fight it — they share the
 * keystroke and the user can toggle).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { semanticSearch, type SearchResult, type SourceKind } from '../../lib/semantic-search';

interface Props {
  open: boolean;
  onClose: () => void;
}

const KIND_META: Record<SourceKind, { label: string; icon: string; route: (id: string) => string | null }> = {
  email_signal: { label: 'Email signal',     icon: '✉',  route: () => '/dashboard/actions' },
  deal:         { label: 'Deal',             icon: '💼', route: (id) => `/dashboard/pipeline?focus=${encodeURIComponent(id)}` },
  action:       { label: 'Action / draft',   icon: '⚡', route: (id) => `/dashboard/actions?focus=${encodeURIComponent(id)}` },
  memory:       { label: 'Agent memory',     icon: '🧠', route: () => '/dashboard/settings' },
  contract:     { label: 'Contract',         icon: '📄', route: () => '/dashboard/contracts' },
  note:         { label: 'Note',             icon: '📝', route: () => '/dashboard/actions' },
  transcript:   { label: 'Meeting',          icon: '📅', route: () => '/dashboard' },
  client:       { label: 'Client',           icon: '👤', route: (id) => `/dashboard/clients/${encodeURIComponent(id)}` },
  invoice:      { label: 'Invoice',          icon: '💰', route: () => '/dashboard/revenue' },
  document:          { label: 'Document',         icon: '📑', route: () => '/dashboard/contracts' },
  sales_activity:    { label: 'Sales activity',   icon: '📞', route: () => '/sales/meetings' },
  meeting_signal:    { label: 'Meeting signal',   icon: '🔔', route: () => '/dashboard/meetings' },
  meeting_transcript:{ label: 'Transcript',       icon: '🎙', route: () => '/dashboard/meetings' },
  meeting_promise:   { label: 'Promise',          icon: '🤝', route: () => '/dashboard/meetings' },
};

export function SemanticSearchModal({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Auto-focus input + reset state on open
  useEffect(() => {
    if (open) {
      // Remember what was focused so we can restore it when the palette closes.
      restoreFocusRef.current = document.activeElement as HTMLElement | null;
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
      setHighlighted(0);
      setError(null);
      restoreFocusRef.current?.focus?.();
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await semanticSearch(query, { limit: 15, minSimilarity: 0.35 });
        setResults(r.results);
        setHighlighted(0);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  const goTo = useCallback((r: SearchResult) => {
    const meta = KIND_META[r.source_kind];
    const route = meta?.route(r.source_id);
    if (route) {
      navigate(route);
      onClose();
    }
  }, [navigate, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((i) => Math.min(results.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((i) => Math.max(0, i - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const r = results[highlighted];
        if (r) goTo(r);
      } else if (e.key === 'Tab') {
        // Trap focus within the dialog.
        const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
          'input, button, a[href], [tabindex]:not([tabindex="-1"])',
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, results, highlighted, goTo, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-200 bg-black/70 backdrop-blur-xs flex items-start justify-center p-4 sm:pt-[10vh]"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search everything"
        className="w-full max-w-2xl rounded-2xl border border-border bg-bg-0 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <span className="text-ink-dim text-lg">🔎</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search everything — emails, deals, contracts, notes, memories…"
            className="flex-1 bg-transparent text-base text-ink placeholder:text-ink-dim outline-hidden"
          />
          <kbd className="hidden sm:inline-block text-2xs bg-bg-2 border border-border rounded-sm px-1.5 py-0.5 text-ink-dim">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-ink-dim">Searching…</div>
          )}
          {!loading && error && (
            <div className="px-4 py-4 text-xs text-danger">{error}</div>
          )}
          {!loading && !error && query.trim().length >= 2 && results.length === 0 && (
            <div className="px-4 py-6 text-center">
              <div className="text-sm text-ink mb-1">No matches</div>
              <div className="text-2xs text-ink-dim">Try different words, or run the backfill in Settings if your data hasn't been embedded yet.</div>
            </div>
          )}
          {!loading && results.length === 0 && query.trim().length < 2 && (
            <div className="px-4 py-6 text-center text-xs text-ink-dim">
              Type at least 2 characters. Searches across emails, deals, contracts, notes, and agent memories — semantically, not just keyword.
            </div>
          )}
          {results.length > 0 && (
            <ul className="py-1">
              {results.map((r, i) => {
                const meta = KIND_META[r.source_kind];
                const isActive = i === highlighted;
                const score = Math.round(r.similarity * 100);
                return (
                  <li key={r.id}>
                    <button
                      onClick={() => goTo(r)}
                      onMouseEnter={() => setHighlighted(i)}
                      className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${
                        isActive ? 'bg-primary/10' : 'hover:bg-surface-1'
                      }`}
                    >
                      <div className="shrink-0 mt-0.5 text-md">{meta?.icon ?? '·'}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">{meta?.label ?? r.source_kind}</span>
                          <span className="text-2xs font-mono text-success">{score}% match</span>
                        </div>
                        <p className="text-sm text-ink leading-snug line-clamp-2">{r.content.slice(0, 200)}</p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border bg-surface-1/40 px-4 py-2 flex items-center justify-between text-2xs text-ink-dim">
          <div className="flex items-center gap-3">
            <span><kbd className="bg-bg-2 border border-border rounded-sm px-1.5 py-0.5">↑↓</kbd> navigate</span>
            <span><kbd className="bg-bg-2 border border-border rounded-sm px-1.5 py-0.5">↵</kbd> open</span>
          </div>
          <span>Semantic · powered by embeddings</span>
        </div>
      </div>
    </div>
  );
}

/** Hook + provider to mount the modal globally and bind ⌘/Ctrl+Shift+K.
 *  Plain ⌘/Ctrl+K is reserved for the Command Palette (industry standard). */
export function useSemanticSearchHotkey(): { open: boolean; setOpen: (v: boolean) => void } {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // ⌘⇧K on mac / Ctrl+Shift+K elsewhere — semantic search
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        const tag = (document.activeElement?.tagName ?? '').toUpperCase();
        const isField = tag === 'INPUT' || tag === 'TEXTAREA' || (document.activeElement as HTMLElement | null)?.isContentEditable;
        if (!isField) {
          e.preventDefault();
          setOpen((v) => !v);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  return { open, setOpen };
}
